import React from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';

/** 'ts' ISO 8601 -> 'DD/MM HH:mm:ss', sem depender do fuso do navegador. */
function formatLastTs(ts) {
  if (!ts || ts.length < 19) return ts || '—';
  const datePart = ts.slice(0, 10);
  const timePart = ts.slice(11, 19);
  const [, month, day] = datePart.split('-');
  return `${day}/${month} ${timePart}`;
}

/**
 * TopErrorsList - Top erros agrupados por mensagem + endpoint (summary.top_errors)
 *
 * Clicar em um item aplica a busca (q = msg) e limpa o filtro de canal, deixando a
 * tabela de logs mostrar todas as ocorrências daquele erro no dia.
 *
 * @param {Object} props
 * @param {Array<{msg: string, endpoint: string, count: number, last_ts: string}>} props.topErrors
 * @param {Function} props.onSelect - (msg: string) => void
 */
function TopErrorsList({ topErrors = [], onSelect }) {
  return (
    <Paper
      sx={{
        borderRadius: '12px',
        border: '1px solid #E8ECF0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E8ECF0', display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReportOutlinedIcon fontSize="small" sx={{ color: '#C62828' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Erros mais frequentes</Typography>
      </Box>

      {topErrors.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Nenhum erro agrupado neste dia.
        </Typography>
      ) : (
        topErrors.map((group, index) => (
          <React.Fragment key={`${group.msg}-${group.endpoint}-${index}`}>
            {index > 0 && <Divider />}
            <Box
              onClick={() => onSelect && onSelect(group.msg)}
              sx={{
                p: 1.5,
                cursor: onSelect ? 'pointer' : 'default',
                '&:hover': onSelect ? { bgcolor: 'action.hover' } : undefined,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                  {group.msg}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#C62828', flexShrink: 0 }}>
                  {group.count}×
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {group.endpoint || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatLastTs(group.last_ts)}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        ))
      )}
    </Paper>
  );
}

export default TopErrorsList;
